import React, { useMemo } from "react";
import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { PageContainer, formatCurrency } from "components/shared";
import { useGetSalesQuery } from "state/api";

const Monthly = () => {
  const theme = useTheme();
  const p = theme.palette;
  const { data, isLoading } = useGetSalesQuery();

  const series = useMemo(
    () =>
      (data?.data?.monthlyData ?? []).map((m) => ({
        label: m.label || m.month,
        revenue: m.totalRevenue ?? m.totalSales ?? 0,
        units: m.totalUnits ?? 0,
      })),
    [data]
  );

  const axisStyle = { fontSize: 12, fill: p.text.secondary };

  return (
    <PageContainer title="Monthly Sales" subtitle="Revenue and units sold per month">
      <Card>
        <CardContent>
          <Box sx={{ height: "68vh" }}>
            {isLoading ? null : series.length === 0 ? (
              <Empty />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 16, right: 24, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={p.divider} />
                  <XAxis dataKey="label" tick={axisStyle} stroke={p.divider} />
                  <YAxis
                    yAxisId="left"
                    tick={axisStyle}
                    stroke={p.divider}
                    width={72}
                    tickFormatter={(v) => formatCurrency(v).replace(/\.00$/, "")}
                  />
                  <YAxis yAxisId="right" orientation="right" tick={axisStyle} stroke={p.divider} width={40} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: p.background.paper,
                      border: `1px solid ${p.divider}`,
                      borderRadius: 8,
                      color: p.text.primary,
                    }}
                    formatter={(value, name) => (name === "Revenue" ? formatCurrency(value) : value)}
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="revenue"
                    name="Revenue"
                    stroke={p.primary.main}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="units"
                    name="Units"
                    stroke={p.secondary.main}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Box>
        </CardContent>
      </Card>
    </PageContainer>
  );
};

const Empty = () => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "text.secondary" }}>
    <Typography variant="body2">No sales data yet</Typography>
  </Box>
);

export default Monthly;
